/**
 * The ONE ordering for nav dropdown entries: rows WITH a set position first
 * (`modules.priority`, ascending), then every row without one, A–Z by display
 * name. Priority 0 means "no position".
 *
 * Ruling, Aaron 2026-09-29 (wizard D190, Mustang H198): *"Menus follow a set
 * order"* — asked as "move expences down below pilot roster" on Mustang's
 * Pilots menu. An alphabetical menu cannot express that; a set position can.
 * Same day, on the 0 question: *"Treat 0 as 'no position'"* — a 0 or a missing
 * priority sorts AFTER the positioned rows, A–Z among themselves.
 *
 * Why 0 is not a position. `modules.priority` is NOT NULL DEFAULT 0, so 0 is
 * what every row nobody ordered carries. From 2026-08-30 (c48cdad68) both menu
 * surfaces sorted by NAME only, because the order /v6/nav delivered read as
 * noise: on g2 Flight Admin opened "Load Sheets, Aircraft Roster, Bookings, …"
 * and Pilots led with "Load Sheets". That order was never random — get_nav
 * (app/nav/crud.py) emits rows by `modules.priority` — it was PRIORITY 0
 * leading a menu otherwise numbered 120–520. Reading 0 as "unset" keeps that
 * noise gone for today's rows AND for every row seeded tomorrow without a
 * position, which lands at the bottom rather than the top.
 *
 * - An all-zero menu (nobody ordered it) reads exactly as the old name sort.
 * - Ties between positioned rows are A–Z.
 * - Also without a position: the rows get_nav fabricates (Journals, a relocated
 *   Xero — sent as null), the client-side Maintenance → MRO Work Orders, and
 *   every mode `subMenus` entry (no row at all), so sub-menus remain A–Z.
 * - To move an entry, give its ROW a priority (a mutation card on a prod
 *   tenant); never reorder in code.
 * - Sorts the client side rather than trusting the wire order: get_nav orders
 *   by name instead when config `nav_alphabetical_order` is set, and its
 *   appends land wherever the code put them.
 *
 * Sorts a COPY: `children` and `subMenus` are `ReadonlyArray` held in nav
 * context, so an in-place `.sort()` would mutate shared state (and not compile).
 *
 * `localeCompare` rather than `<` so a non-ASCII module name orders the way a
 * reader expects rather than by code point.
 */
export interface NavOrderable {
  name: string;
  /** `modules.priority` from /v6/nav; 0, null or absent = no set position. */
  priority?: number | null;
}

const position = (item: NavOrderable): number =>
  typeof item.priority === "number" &&
  Number.isFinite(item.priority) &&
  item.priority !== 0
    ? item.priority
    : Number.POSITIVE_INFINITY;

export const bySetOrder = <T extends NavOrderable>(
  items: ReadonlyArray<T>
): T[] =>
  [...items].sort((a, b) => {
    const pa = position(a);
    const pb = position(b);
    if (pa !== pb) return pa < pb ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

export default bySetOrder;
